import React from "react";
import "./AssetOverviewPage.scss";
import {
    Button,
    Card,
    CardBody,
    CardHeader, DemandListCell,
    Dropdown,
    Input,
    List,
    StateOptions,
    StandardListCell,
    StateListCell,
    StateSelectMenu
} from "gd-react";
import getFormat from "../../services/formatter";
import GridDuck from "gridduck";
import history from "../../meta/history";
import RulePageTimeswitch from "../rule/RulePageTimeswitch";
import RulePageAlert from "../rule/RulePageAlert";
import RulePageThermostat from "../rule/RulePageThermostat";
import gatewayCommandSocket, {ZIGBEE_STATE_METHOD} from "../../services/gatewayCommandSocket";

// The skus that show the Phase breakdown section.
const PHASE_SKUS = ['SEN-POW-OWON', 'SEN-POW-OWON-OLD'];

// Converter property on the pi-duck -> asset field the section renders. The PC321 declares power_factor in %, the asset stores a 0-1 fraction.
const PHASE_PROPERTIES = [
    {property: 'power_l1', field: 'demandPhaseOne'},
    {property: 'power_l2', field: 'demandPhaseTwo'},
    {property: 'power_l3', field: 'demandPhaseThree'},
    {property: 'power_factor_l1', field: 'pfPhaseOne', scale: 0.01},
    {property: 'power_factor_l2', field: 'pfPhaseTwo', scale: 0.01},
    {property: 'power_factor_l3', field: 'pfPhaseThree', scale: 0.01},
];

// The hub spells an address 0015BC002F009618 and the pi-duck 0x0015bc002f009618.
function sameAddress(a, b) {
    let strip = (value) => String(value || '').replace(/^0x/i, '').toLowerCase();
    return strip(a) !== '' && strip(a) === strip(b);
}

// Pulls the phase fields out of a zigbee.getState answer; older firmware ignores `id` and answers for every device, so match by address.
export function phasesFromSnapshot(snapshot, code) {
    let devices = (snapshot && snapshot.devices) || {};
    let address = Object.keys(devices).find(a => sameAddress(a, code));
    if (!address) return null;
    let state = devices[address].state || {};
    let phases = {lastSeen: devices[address].lastSeen};
    PHASE_PROPERTIES.forEach(function (p) {
        let value = Number(state[p.property]);
        if (state[p.property] === null || state[p.property] === undefined || !Number.isFinite(value)) return;
        phases[p.field] = p.scale ? Math.round(value * p.scale * 1000) / 1000 : value;
    });
    return phases;
}

const toCamel = (s) => {
    return s.replace(/([-_][a-z])/ig, ($1) => {
        return $1.toUpperCase()
            .replace('-', '')
            .replace('_', '');
    });
};

function skuDetails(sku) {
    let type = StateOptions.find(sO => sO.type === sku);
    if (type) {
        // BACnet/IP devices use several skus but share one product image.
        let imageName = sku && sku.indexOf('BACNET_IP') === 0 ? 'BACNET_IP'
            // Both Modbus transports share one product image
            : sku === 'MODBUS_TCP' ? 'MODBUS'
            : sku;
        return {
            name: type.label,
            image: '/images/products/' + imageName + '.png'
        }
    } else {
        return {
            name: 'Unknown',
            image: '/images/products/unknown.png'
        }
    }
}

class AssetOverviewPage extends React.Component {
    constructor(props) {
        super(props);
        let self = this;
        this.updatedExternally = this.updatedExternally.bind(this);
        this.goToAsset = this.goToAsset.bind(this);
        this.getList = this.getList.bind(this);
        this.getParentList = this.getParentList.bind(this);
        this.getAvailableList = this.getAvailableList.bind(this);
        this.identify = this.identify.bind(this);
        this.getPhaseBreakdown = this.getPhaseBreakdown.bind(this);
        this.phaseValue = this.phaseValue.bind(this);
        if (props.item) {
            props.item.on('updated', this.updatedExternally);
            if (props.item.triggerRules && props.item.alertRules && props.item.advancedRules) {
                Promise.all(props.item.triggerRules.concat(props.item.alertRules).concat(props.item.advancedRules).map(rId => GridDuck.getRule({id: rId})))
                    .then(function (rules) {
                        self.setState({rules: rules});
                    });
            }
            if (props.item.parentAsset) {
                GridDuck.getAsset({id: props.item.parentAsset}).then(function (parentAsset) {
                    self.setState({parentName: parentAsset.name});
                });
            }
            this.state = {
                updateRef: 0,
                asset: props.item,
                childrenFilters: [
                    {
                        field: 'parentAsset',
                        value: this.props.item.id
                    }
                ]
            };
        } else {
            this.state = {updateRef: 0};
        }
        GridDuck.getAccount({id: GridDuck.userId}).then(function (account) {
            self.setState({
                orgPermission: account.orgPermission,
            })
        });

    }

    componentDidUpdate(prevProps, prevState, snapshot) {
        let self = this;
        if (prevProps !== this.props || this.props.item.id !== prevProps.item.id) {
            if (prevProps.item) {
                prevProps.item.off('updated', this.updatedExternally);
            }
            this.setState({
                asset: this.props.item,
                childrenFilters: [
                    {
                        field: 'parentAsset',
                        value: this.props.item.id
                    }
                ]
            });
            this.props.item.on('updated', this.updatedExternally);
            if (this.props.item.id !== (prevProps.item && prevProps.item.id)) {
                this.setState({piDuckGatewayId: null, rpcPhases: null});
                this.loadPhaseSource(this.props.item);
            }
            if (this.props.item.parentAsset) {
                GridDuck.getAsset({id: this.props.item.parentAsset}).then(function (parentAsset) {
                    self.setState({parentName: parentAsset.name});
                });
            } else {
                self.setState({parentName: null});
            }
        }
    }

    componentDidMount() {
        let self = this
        this.loadPhaseSource(this.props.item);
        this.updateInterval = setInterval(() => this.setState({updateRef: this.state.updateRef + 1}), 60000);
        if (this.props.item.modbusId) {
            GridDuck.getModbusModel({id: this.props.item.modbusId}).then((res) => {
                    self.setState({modbusData: res})
                }
            )
        }
    }


    componentWillUnmount() {
        if (this.props.item && this.props.item.off) {
            this.props.item.off('updated', this.updatedExternally);
        }
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        this.unmounted = true;
    }

    updatedExternally(field) {
        let self = this;
        const allowedFields = [
            'name',
            'sku',
            'code',
            'demand',
            'consumption',
            'signalStrength',
            'lastContacted',
            'demandPhaseOne',
            'demandPhaseTwo',
            'demandPhaseThree',
            'pfPhaseOne',
            'pfPhaseTwo',
            'pfPhaseThree'
        ]
        if (allowedFields.indexOf(field) !== -1) {
            if (field === 'demandPhaseOne') {
                if (self.getBreakdownResolve) {
                    self.getBreakdownResolve();
                }
                this.setState({gettingPhaseOne: false});
            }
            if (field === 'demandPhaseTwo') {
                if (self.getBreakdownResolve) {
                    self.getBreakdownResolve();
                }
                this.setState({gettingPhaseTwo: false});
            }
            if (field === 'demandPhaseThree') {
                if (self.getBreakdownResolve) {
                    self.getBreakdownResolve();
                }
                this.setState({gettingPhaseThree: false});
            }
            if (field === 'pfPhaseOne') {
                if (Math.abs(this.props.item.demandPhaseOne) >= 5) {
                    if (self.getBreakdownResolve) {
                        self.getBreakdownResolve();
                    }
                    this.setState({gettingPfOne: false});
                }
            }
            if (field === 'pfPhaseTwo') {
                if (Math.abs(this.props.item.demandPhaseTwo) >= 5) {
                    if (self.getBreakdownResolve) {
                        self.getBreakdownResolve();
                    }
                    this.setState({gettingPfTwo: false});
                }
            }
            if (field === 'pfPhaseThree') {
                if (Math.abs(this.props.item.demandPhaseThree) >= 5) {
                    if (self.getBreakdownResolve) {
                        self.getBreakdownResolve();
                    }
                    this.setState({gettingPfThree: false});
                }
            }
            this.setState({updateRef: this.state.updateRef++});
        }
    }

    goToAsset(asset) {
        history.push("/device/" + asset.id);
    }

    async getAvailableList(retrievalParams) {
        let paramsFromComponent = retrievalParams.filters || []
        retrievalParams.filters = paramsFromComponent.concat([{field: 'siteId', value: this.props.item.siteId}]);
        return GridDuck.getAssets(retrievalParams);
    }


    getParentList(retrievalParams) {
        return GridDuck.getAssets(retrievalParams);
    }

    getList(retrievalParams) {
        let paramsFromComponent = retrievalParams.filters || []
        retrievalParams.filters = paramsFromComponent.concat([
            {
                field: 'virtualParentAsset',
                value: this.props.item.id
            },
            {
                field: 'showVirtualMergedChildren',
                value: true
            }
        ]);
        return GridDuck.getAssets(retrievalParams);
    }

    identify() {
        return GridDuck.createAssetIdentification({assetId: this.state.asset.id})
    }

    // Only a phase meter behind a pi-duck asks: that gateway answers over the command channel rather than the cloud flag.
    loadPhaseSource(asset) {
        let self = this;
        if (!asset || !asset.gatewayId || PHASE_SKUS.indexOf(asset.sku) === -1) return;
        GridDuck.getGateway({id: asset.gatewayId})
            .then(function (gateway) {
                if (self.unmounted || !self.props.item || self.props.item.id !== asset.id) return;
                if (gatewayCommandSocket.isPiDuck(gateway)) self.setState({piDuckGatewayId: gateway.id});
            })
            // The legacy path still works for the render, so a failed lookup is not worth surfacing.
            .catch(function () {});
    }

    // The pi-duck's answer wins over the asset's last-logged figure, which is only there when per-phase logging is on.
    phaseValue(field) {
        let rpc = this.state.rpcPhases;
        if (rpc && rpc[field] !== undefined) return rpc[field];
        return this.state.asset ? this.state.asset[field] : undefined;
    }

    // The pi-duck answers with what the meter last reported, so say how old that is.
    phaseSourceNote() {
        if (this.state.rpcPhaseError) {
            return <p className={"asset-data"} style={{color: '#c0392b'}}>{this.state.rpcPhaseError}</p>;
        }
        let rpc = this.state.rpcPhases;
        if (!rpc) return null;
        if (!['demandPhaseOne', 'demandPhaseTwo', 'demandPhaseThree'].some(f => rpc[f] !== undefined)) {
            return <p className={"asset-data"}>The meter has not reported any per-phase readings yet.</p>;
        }
        return rpc.lastSeen
            // herdsman's lastSeen is in ms; the formatter takes unix seconds.
            ? <p className={"asset-data"}>As last reported by the meter at {getFormat('lastContacted')(Math.round(rpc.lastSeen / 1000))}</p>
            : null;
    }

    getPiDuckPhaseBreakdown() {
        let self = this;
        let asset = this.state.asset;
        this.setState({rpcPhaseError: null});
        return gatewayCommandSocket.invoke(this.state.piDuckGatewayId, ZIGBEE_STATE_METHOD, {id: asset.code})
            .then(function (snapshot) {
                let phases = phasesFromSnapshot(snapshot, asset.code);
                if (!phases) {
                    let err = new Error('Device not on the network');
                    err.code = 'NOT_FOUND';
                    throw err;
                }
                if (!self.unmounted) self.setState({rpcPhases: phases});
            })
            .catch(function (err) {
                if (!self.unmounted) {
                    self.setState({rpcPhaseError: err.code === 'NOT_FOUND'
                        ? 'The hub does not have this meter on its network.'
                        : 'Could not read the meter from the hub: ' + err.message});
                }
                // Rethrown so the progressRes button shows the failure.
                throw err;
            });
    }

    getPhaseBreakdown() {
        let self = this;
        if (this.state.piDuckGatewayId) return this.getPiDuckPhaseBreakdown();
        this.setState({
            gettingPhaseOne: true,
            gettingPhaseTwo: true,
            gettingPhaseThree: true,
            gettingPfOne: true,
            gettingPfTwo: true,
            gettingPfThree: true
        });
        self.resolved = false;
        return GridDuck.createGetPhaseBreakdown({assetId: this.state.asset.id})
            .then(function () {
                return new Promise(function (resolve, reject) {
                    self.getBreakdownResolve = function () {
                        self.resolved = true;
                        resolve();
                    }
                    setTimeout(function () {
                        if (!self.resolved) {
                            reject();
                        }
                    }, 10000)
                });
            })
    }
}

export default AssetOverviewPage;
